import type { FC } from 'react';
import React, { useContext, useState } from 'react';
import { EditorContext, ModalContext, RicosContext } from 'ricos-context';
import type { IToolbarItem } from 'ricos-types';
import { ToggleButton } from 'wix-rich-content-toolbars-ui';

import { HTML_BUTTONS } from '../consts';
import type { SliderModalProps } from './SliderModal';

interface Props {
  toolbarItem: IToolbarItem;
  dimension: 'width' | 'height';
  min: number;
  max: number;
  defaultValue: number;
  dataHook?: string;
}

export const DimensionSliderButton: FC<Props> = ({
  toolbarItem,
  dimension,
  dataHook,
  min,
  max,
  defaultValue,
}) => {
  const [referenceElement, setReferenceElement] = useState<HTMLDivElement | null>(null);
  const { t } = useContext(RicosContext) || {};
  const modalService = useContext(ModalContext);

  const { icon, tooltip } = toolbarItem.presentation as Record<string, any>;

  const node = (toolbarItem.attributes as Record<string, any>).selectedNode;

  const { getEditorCommands } = useContext(EditorContext);

  const getComponentData = () => {
    const nodeId = node?.attrs.id;
    return getEditorCommands().getBlockComponentData(nodeId) || {};
  };

  const getValue = () => getComponentData().containerData?.[dimension]?.custom;

  const onChange = value => {
    const componentData = getComponentData();
    const containerData = {
      ...componentData.containerData,
      [dimension]: {
        custom: value,
      },
    };

    // to avoid flushSync error
    setTimeout(() => {
      toolbarItem.commands?.click({ value, data: { ...componentData, containerData } });
    }, 0);
  };

  const onClick = () => {
    if (modalService?.isModalOpen(HTML_BUTTONS[dimension])) {
      modalService.closeModal(HTML_BUTTONS[dimension]);
    } else {
      const componentProps: SliderModalProps = {
        onChange,
        min,
        max,
        value: getValue() || defaultValue,
      };
      modalService.openModal(HTML_BUTTONS[dimension], {
        componentProps,
        layout: 'popover',
        positioning: { referenceElement, placement: 'bottom' },
      });
    }
  };

  return (
    <div ref={setReferenceElement}>
      <ToggleButton Icon={icon} onClick={onClick} dataHook={dataHook} tooltip={t(tooltip)} />
    </div>
  );
};
